import { FC, useEffect, useState } from "react";
import { isEqual } from "lodash";
import NewIssueScreen from "./NewIssueScreen";
import { selectCurrentUserId } from "@store/user/userSelectors";
import useAppSelector from "@hooks/useAppSelector";
import { Issue, IssueState, IssueType } from "@store/issue/types";
import { selectAllSpaces } from "@store/space/spaceSelectors";
import { selectIssueById } from "@store/issue/issueSelectors";
import { createIssue, updateIssue } from "@store/issue/issueThunk";
import useAppDispatch from "@hooks/useAppDispatch";
import { generateSections } from "@store/issue/issueHelpers";

interface NewIssueContainerProps {
  onClose: () => void;
  selectedIssueId?: string;
  type: IssueType;
}

const NewIssueContainer: FC<NewIssueContainerProps> = ({
  onClose,
  selectedIssueId,
  type = IssueType.TECHNICAL,
}) => {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(selectCurrentUserId);

  const [issue, setIssue] = useState<Omit<Issue, "id">>({
    spaceId: "",
    label: "",
    authorId: currentUserId,
    reportedBy: "",
    createdAt: new Date().toISOString(),
    priority: false,
    description: "",
    attachments: [],
    state: IssueState.OPEN,
    users: [],
    workLogs: [],
    typeOf: type,
  });
  const spaces = useAppSelector(({ space }) => selectAllSpaces(space));

  let selectedIssue: Issue | undefined = undefined;

  if (selectedIssueId)
    selectedIssue = useAppSelector((state) =>
      selectIssueById(state.issue, selectedIssueId),
    );

  const [hasChanges, setHasChanges] = useState(!selectedIssueId);

  const canSubmit =
    issue.spaceId !== undefined &&
    issue.spaceId !== "" &&
    issue.label !== undefined &&
    issue.label?.length > 1 &&
    issue.reportedBy !== undefined &&
    issue.reportedBy?.length > 1 &&
    hasChanges;

  const onSubmit = () => {
    const { attachments, ...newIssue } = issue;
    if (selectedIssue) {
      dispatch(updateIssue(selectedIssue, newIssue, attachments));
    } else {
      dispatch(createIssue({ ...newIssue, attachments: [] }, attachments));
    }
    onClose();
  };

  useEffect(() => {
    if (!selectedIssue) return;
    setIssue(selectedIssue);
  }, [selectedIssue]);

  useEffect(() => {
    if (!selectedIssue) return;
    setHasChanges(!isEqual(selectedIssue, issue));
  }, [selectedIssue, issue]);

  return (
    <NewIssueScreen
      sections={generateSections(spaces)}
      issue={issue}
      setIssue={setIssue}
      onSubmit={onSubmit}
      canSubmit={canSubmit}
      isInEditMode={!!selectedIssueId}
    />
  );
};

export default NewIssueContainer;
